import { useState, useEffect, useRef, useMemo } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "../../../styles/quill-overrides.css";
import { Quill } from "shared/config/quillSetup";
import { Chip } from "@heroui/react";
import {
  Scribble,
  RenovationProject,
  ProjectPart,
} from "shared/types/scribble";
import {
  Buildings,
  User,
  Hammer,
  Trash,
  Wrench,
  Cloud,
  CheckCircle,
} from "@phosphor-icons/react";
import { UniversalPickerModal } from "shared/components/UniversalPickerModal";
import { BuildingDTO } from "shared/types/building";
import { Contact } from "shared/types/contacts";
import { useSession } from "@clerk/clerk-react";
import { toast } from "react-toastify";

interface NoteEditorSimpleProps {
  note: Scribble | null;
  onSave: (note: Partial<Scribble>) => Promise<void>;
  onDelete?: (note: Scribble) => Promise<void>;
}

export const NoteEditorSimple: React.FC<NoteEditorSimpleProps> = ({
  note,
  onSave,
  onDelete,
}) => {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [tags, setTags] = useState("");
  const [linkedBuilding, setLinkedBuilding] = useState<
    BuildingDTO | undefined
  >();
  const [linkedContacts, setLinkedContacts] = useState<Contact[]>([]);
  const [linkedRenovationProject, setLinkedRenovationProject] = useState<
    RenovationProject | undefined
  >();
  const [linkedProjectPart, setLinkedProjectPart] = useState<
    ProjectPart | undefined
  >();
  const [isSaving, setIsSaving] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showBuildingPicker, setShowBuildingPicker] = useState(false);
  const [showContactPicker, setShowContactPicker] = useState(false);
  const [showRenovationProjectPicker, setShowRenovationProjectPicker] =
    useState(false);
  const [showProjectPartPicker, setShowProjectPartPicker] = useState(false);

  const { session } = useSession();
  const saveTimeoutRef = useRef<NodeJS.Timeout>();
  const quillRef = useRef<ReactQuill>(null);
  const previousNoteIdRef = useRef<number | undefined>(note?.id);
  const isSavingRef = useRef(false);

  // Refs to capture current note data before switching
  const currentNoteDataRef = useRef({
    title,
    content,
    tags,
    building: linkedBuilding,
    contacts: linkedContacts,
    renovationProject: linkedRenovationProject,
    renovationProjectPart: linkedProjectPart,
  });

  // Update refs whenever data changes
  useEffect(() => {
    currentNoteDataRef.current = {
      title,
      content,
      tags,
      building: linkedBuilding,
      contacts: linkedContacts,
      renovationProject: linkedRenovationProject,
      renovationProjectPart: linkedProjectPart,
    };
  }, [
    title,
    content,
    tags,
    linkedBuilding,
    linkedContacts,
    linkedRenovationProject,
    linkedProjectPart,
  ]);

  const handleImageUpload = async (file: File) => {
    try {
      // Compress image if needed
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target?.result as string;
        const quill = quillRef.current?.getEditor();
        if (quill) {
          const range = quill.getSelection();
          const index = range?.index ?? quill.getLength();
          quill.insertEmbed(index, "image", base64);

          // Apply default left alignment after a brief delay to ensure image is rendered
          setTimeout(() => {
            const img = quill.root.querySelector(
              `img[src="${base64}"]`,
            ) as HTMLImageElement;
            if (img) {
              img.style.float = "left";
              img.style.display = "inline";
              img.style.margin = "0 1em 1em 0";
            }
          }, 10);

          quill.setSelection(index + 1, 0);
          setHasUnsavedChanges(true);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error("Error processing image:", error);
      toast.error("Fout bij uploaden van afbeelding");
      throw error;
    }
  };

  // Image handler for toolbar button
  const imageHandler = () => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];
      if (file) {
        try {
          await handleImageUpload(file);
        } catch (error) {
          console.error("Error uploading image:", error);
        }
      }
    };
  };

  // Handle paste and drop events for images
  useEffect(() => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    const handlePaste = (e: ClipboardEvent) => {
      const clipboardData = e.clipboardData;
      if (!clipboardData) return;

      const items = Array.from(clipboardData.items);
      const imageItem = items.find((item) => item.type.startsWith("image/"));

      if (imageItem) {
        e.preventDefault();
        const file = imageItem.getAsFile();
        if (file) {
          handleImageUpload(file);
        }
      }
    };

    const handleDrop = (e: DragEvent) => {
      e.preventDefault();
      const files = e.dataTransfer?.files;
      if (files && files.length > 0) {
        const imageFile = Array.from(files).find((file) =>
          file.type.startsWith("image/"),
        );
        if (imageFile) {
          handleImageUpload(imageFile);
        }
      }
    };

    const handleDragOver = (e: DragEvent) => {
      e.preventDefault();
    };

    const editorElement = quill.root;
    editorElement.addEventListener("paste", handlePaste);
    editorElement.addEventListener("drop", handleDrop);
    editorElement.addEventListener("dragover", handleDragOver);

    return () => {
      editorElement.removeEventListener("paste", handlePaste);
      editorElement.removeEventListener("drop", handleDrop);
      editorElement.removeEventListener("dragover", handleDragOver);
    };
  }, []);

  // Handle note switching with background save
  useEffect(() => {
    const currentNoteId = note?.id;
    const previousNoteId = previousNoteIdRef.current;

    // If note ID changed, we're switching notes
    if (previousNoteId !== currentNoteId) {
      // FIRST: Immediately load the new note data (do this BEFORE anything else)
      if (note) {
        setTitle(note.title || "");
        setContent(note.content || "");
        setTags(note.tags || "");
        setLinkedBuilding(note.building);
        setLinkedContacts(note.contacts || []);
        setLinkedRenovationProject(note.renovationProject);
        setLinkedProjectPart(note.renovationProjectPart);
        setHasUnsavedChanges(false);
        setIsTyping(false);
      } else {
        // Clear form when no note selected
        setTitle("");
        setContent("");
        setTags("");
        setLinkedBuilding(undefined);
        setLinkedContacts([]);
        setLinkedRenovationProject(undefined);
        setLinkedProjectPart(undefined);
        setHasUnsavedChanges(false);
        setIsTyping(false);
      }

      // THEN: Save old note in background (after state is updated)
      // This handles BOTH existing notes (with ID) and new notes (without ID)
      if (hasUnsavedChanges) {
        // Clear any pending auto-save timeout
        if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current);
        }

        // Capture the data from the OLD note (from refs)
        const oldNoteData = currentNoteDataRef.current;

        // Schedule save for next tick to not block current render
        setTimeout(() => {
          const saveInBackground = async () => {
            try {
              const noteData: Partial<Scribble> = {
                id: previousNoteId, // Will be undefined for new notes
                title: oldNoteData.title || "Naamloos",
                content: oldNoteData.content,
                tags: oldNoteData.tags,
                building: oldNoteData.building,
                contacts: oldNoteData.contacts,
                renovationProject: oldNoteData.renovationProject,
                renovationProjectPart: oldNoteData.renovationProjectPart,
                userId: session?.user?.id,
              };

              // This will create new note if id is undefined, or update if id exists
              await onSave(noteData);
            } catch (error) {
              console.error("Error saving previous note in background:", error);
              // Silent failure - don't interrupt user
            }
          };

          saveInBackground();
        }, 0);
      }

      // Update previous note ID ref
      previousNoteIdRef.current = currentNoteId;
    }
  }, [note?.id]); // Only depend on note?.id to minimize re-renders

  // Auto-save with debounce
  useEffect(() => {
    if (!hasUnsavedChanges) return;

    // Show typing indicator immediately
    setIsTyping(true);

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      handleSave();
    }, 2000);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [
    title,
    content,
    tags,
    linkedBuilding,
    linkedContacts,
    linkedRenovationProject,
    linkedProjectPart,
  ]);

  const handleSave = async () => {
    // Prevent concurrent saves
    if (isSavingRef.current) return;

    try {
      isSavingRef.current = true;
      setIsSaving(true);
      setIsTyping(false); // Clear typing indicator when save starts

      const noteData: Partial<Scribble> = {
        id: note?.id,
        title: title || "Naamloos",
        content,
        tags,
        building: linkedBuilding,
        contacts: linkedContacts,
        renovationProject: linkedRenovationProject,
        renovationProjectPart: linkedProjectPart,
        userId: session?.user?.id,
      };

      await onSave(noteData);
      setHasUnsavedChanges(false);
      // Don't show toast on successful save - icon indicator is enough
    } catch (error) {
      console.error("Error saving note:", error);
      toast.error("Fout bij opslaan van notitie");
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  };

  const handleDelete = () => {
    if (!note || !onDelete) return;
    // Just call onDelete - parent component handles confirmation and toasts
    onDelete(note);
  };

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, false] }],
          ["bold", "italic", "underline"],
          [{ list: "ordered" }, { list: "bullet" }],
          ["link", "image"],
          ["clean"],
        ],
        handlers: {
          image: imageHandler,
        },
      },
      imageResize: {
        parchment: Quill.import("parchment"),
        modules: ["Resize", "DisplaySize", "Toolbar"],
      },
    }),
    [],
  );

  if (!note && !session?.user?.id) {
    return (
      <div className="bg-neutral-primary/[0.01] flex h-full items-center justify-center p-8">
        <div className="text-neutral-primary/40 text-center">
          <p className="text-sm">Geen notitie geselecteerd</p>
          <p className="mt-1 text-xs">
            Selecteer een notitie of maak een nieuwe
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex h-full flex-col bg-white">
        {/* Minimal header */}
        <div className="border-neutral-primary/5 flex items-center justify-between border-b px-6 py-2">
          <div className="flex items-center gap-2">
            {(isTyping || isSaving) && (
              <>
                <Cloud
                  weight="duotone"
                  size={20}
                  className="text-brick-primary animate-spin"
                  style={{ animationDuration: "2s" }}
                />
                <span className="text-brick-primary text-sm">Opslaan...</span>
              </>
            )}
            {!isTyping && !isSaving && !hasUnsavedChanges && note && (
              <>
                <CheckCircle
                  weight="duotone"
                  size={20}
                  className="text-moss-primary"
                />
                <span className="text-moss-primary text-sm">Opgeslagen</span>
              </>
            )}
          </div>
          {note && onDelete && (
            <button
              onClick={handleDelete}
              className="text-neutral-primary/40 rounded p-1.5 transition-colors hover:bg-red-50 hover:text-red-500"
              title="Notitie verwijderen"
            >
              <Trash weight="regular" size={16} />
            </button>
          )}
        </div>

        {/* Editor content - clean Bear style */}
        <div className="flex-1 overflow-y-auto bg-white">
          <div className="mx-auto max-w-5xl px-8 py-8">
            {/* Title */}
            <input
              type="text"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                setHasUnsavedChanges(true);
              }}
              placeholder="Titel"
              className="text-neutral-primary placeholder:text-neutral-primary/20 mb-6 w-full border-none bg-transparent text-4xl font-bold tracking-tight outline-none"
            />

            {/* Tags input */}
            <input
              type="text"
              value={tags}
              onChange={(e) => {
                setTags(e.target.value);
                setHasUnsavedChanges(true);
              }}
              placeholder="#tags (gescheiden door komma's)"
              className="text-brick-primary placeholder:text-neutral-primary/30 mb-6 w-full border-none bg-transparent text-base font-medium outline-none"
            />

            {/* Linked resources chips */}
            {(linkedBuilding ||
              linkedContacts.length > 0 ||
              linkedRenovationProject ||
              linkedProjectPart) && (
              <div className="border-neutral-primary/5 mb-4 flex flex-wrap gap-2 border-b pb-4">
                {linkedBuilding && (
                  <Chip
                    onClose={() => {
                      setLinkedBuilding(undefined);
                      setHasUnsavedChanges(true);
                    }}
                    variant="flat"
                    size="sm"
                    classNames={{
                      base: "bg-neutral-primary/5 cursor-pointer hover:bg-neutral-primary/10",
                      content: "text-xs text-neutral-primary/70",
                    }}
                    startContent={<Buildings weight="duotone" size={14} />}
                    onClick={() =>
                      window.open(`/building/${linkedBuilding.id}`, "_blank")
                    }
                  >
                    {linkedBuilding.id}
                  </Chip>
                )}
                {linkedContacts.map((contact) => (
                  <Chip
                    key={contact.id}
                    onClose={() => {
                      setLinkedContacts((prev) =>
                        prev.filter((c) => c.id !== contact.id),
                      );
                      setHasUnsavedChanges(true);
                    }}
                    variant="flat"
                    size="sm"
                    classNames={{
                      base: "bg-neutral-primary/5 cursor-pointer hover:bg-neutral-primary/10",
                      content: "text-xs text-neutral-primary/70",
                    }}
                    startContent={<User weight="duotone" size={14} />}
                    onClick={() =>
                      window.open(`/contact/${contact.id}`, "_blank")
                    }
                  >
                    {contact.firstName} {contact.lastName}
                  </Chip>
                ))}
                {linkedRenovationProject && (
                  <Chip
                    onClose={() => {
                      setLinkedRenovationProject(undefined);
                      setHasUnsavedChanges(true);
                    }}
                    variant="flat"
                    size="sm"
                    classNames={{
                      base: "bg-neutral-primary/5 cursor-pointer hover:bg-neutral-primary/10",
                      content: "text-xs text-neutral-primary/70",
                    }}
                    startContent={<Hammer weight="duotone" size={14} />}
                    onClick={() =>
                      window.open(
                        `/renovation/${linkedRenovationProject.id}`,
                        "_blank",
                      )
                    }
                  >
                    RENO-{linkedRenovationProject.id} -{" "}
                    {linkedRenovationProject.title}
                  </Chip>
                )}
                {linkedProjectPart && (
                  <Chip
                    onClose={() => {
                      setLinkedProjectPart(undefined);
                      setHasUnsavedChanges(true);
                    }}
                    variant="flat"
                    size="sm"
                    classNames={{
                      base: "bg-neutral-primary/5 cursor-pointer hover:bg-neutral-primary/10",
                      content: "text-xs text-neutral-primary/70",
                    }}
                    startContent={<Wrench weight="duotone" size={14} />}
                    onClick={() => {
                      if (linkedRenovationProject?.id) {
                        window.open(
                          `/renovation/${linkedRenovationProject.id}`,
                          "_blank",
                        );
                      }
                    }}
                  >
                    {linkedProjectPart.partName}
                  </Chip>
                )}
              </div>
            )}

            {/* Quick actions */}
            <div className="mb-6 flex gap-2">
              <button
                onClick={() => setShowBuildingPicker(true)}
                className="text-neutral-primary/50 hover:text-moss-primary hover:bg-moss-primary/5 flex items-center gap-1 rounded px-2 py-1 text-xs transition-colors"
              >
                <Buildings weight="duotone" size={14} />
                <span>Gebouw</span>
              </button>
              <button
                onClick={() => setShowContactPicker(true)}
                className="text-neutral-primary/50 hover:text-moss-primary hover:bg-moss-primary/5 flex items-center gap-1 rounded px-2 py-1 text-xs transition-colors"
              >
                <User weight="duotone" size={14} />
                <span>Contact</span>
              </button>
              <button
                onClick={() => setShowRenovationProjectPicker(true)}
                className="text-neutral-primary/50 hover:text-moss-primary hover:bg-moss-primary/5 flex items-center gap-1 rounded px-2 py-1 text-xs transition-colors"
              >
                <Hammer weight="duotone" size={14} />
                <span>Renovatieproject</span>
              </button>
              <button
                onClick={() => setShowProjectPartPicker(true)}
                className="text-neutral-primary/50 hover:text-moss-primary hover:bg-moss-primary/5 flex items-center gap-1 rounded px-2 py-1 text-xs transition-colors"
              >
                <Wrench weight="duotone" size={14} />
                <span>Projectdeel</span>
              </button>
            </div>

            {/* Content editor - wrapped in beautiful container */}
            <div className="prose max-w-none">
              <div className="border-neutral-primary/10 overflow-hidden rounded-lg border shadow-sm">
                <ReactQuill
                  ref={quillRef}
                  theme="snow"
                  value={content}
                  onChange={(value) => {
                    setContent(value);
                    setHasUnsavedChanges(true);
                  }}
                  modules={modules}
                  placeholder="Begin met schrijven..."
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <UniversalPickerModal
        isOpen={showBuildingPicker}
        mode="building"
        title="Selecteer gebouw"
        onClose={() => setShowBuildingPicker(false)}
        onConfirm={(building) => {
          setLinkedBuilding(building);
          setHasUnsavedChanges(true);
          setShowBuildingPicker(false);
        }}
        config={{ allowRemoval: true }}
      />

      <UniversalPickerModal
        isOpen={showContactPicker}
        mode="reporter"
        title="Selecteer contact"
        onClose={() => setShowContactPicker(false)}
        onConfirm={(contact) => {
          if (!linkedContacts.find((c) => c.id === contact.id)) {
            setLinkedContacts((prev) => [...prev, contact]);
            setHasUnsavedChanges(true);
          }
          setShowContactPicker(false);
        }}
        config={{ allowRemoval: false }}
      />

      <UniversalPickerModal
        isOpen={showRenovationProjectPicker}
        mode="renovation-project"
        title="Selecteer renovatieproject"
        onClose={() => setShowRenovationProjectPicker(false)}
        onConfirm={(project) => {
          setLinkedRenovationProject(project);
          setHasUnsavedChanges(true);
          setShowRenovationProjectPicker(false);
        }}
        config={{ allowRemoval: true }}
      />

      <UniversalPickerModal
        isOpen={showProjectPartPicker}
        mode="project-part"
        title="Selecteer projectdeel"
        onClose={() => setShowProjectPartPicker(false)}
        onConfirm={(part) => {
          setLinkedProjectPart(part);
          setHasUnsavedChanges(true);
          setShowProjectPartPicker(false);
        }}
        config={{
          allowRemoval: true,
          currentRenovationProjectId: linkedRenovationProject?.id,
        }}
      />
    </>
  );
};
